/**
 * Form Handler Submission Log
 * Paginated view of all submissions for a single form handler.
 */

import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  getFormHandler,
  getFormSubmissions,
} from "../services/formHandlers.service";
import LoadingSpinner from "../components/common/LoadingSpinner";
import {
  ArrowLeftIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import type { FormSubmission } from "../types/formHandler.types";

const PAGE_SIZE = 50;

export default function FormHandlerSubmissions() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data: handler } = useQuery({
    queryKey: ["form-handler", id],
    queryFn: () => getFormHandler(id!),
    enabled: !!id,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ["form-submissions", id, page],
    queryFn: () => getFormSubmissions(id!, page, PAGE_SIZE),
    enabled: !!id,
  });

  const submissions = data?.submissions || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const statusBadge = (status: FormSubmission["status"]) => {
    if (status === "success") {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
          success
        </span>
      );
    }
    if (status === "error") {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
          error
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
        {status}
      </span>
    );
  };

  const actionIcons = (actionsPerformed?: Record<string, unknown>) => {
    if (!actionsPerformed) {
      return null;
    }
    const icons: React.ReactNode[] = [];
    const checks: Record<string, string> = {
      add_to_lists: "📋",
      add_tags: "🏷️",
      notify_users: "🔔",
      autoresponder: "✉️",
    };
    for (const [key, emoji] of Object.entries(checks)) {
      const action = actionsPerformed[key] as { success?: boolean } | undefined;
      if (action) {
        icons.push(
          <span
            key={key}
            title={`${key}: ${action.success ? "ok" : "failed"}`}
            className={action.success ? "opacity-100" : "opacity-30"}
          >
            {emoji}
          </span>,
        );
      }
    }
    return icons.length > 0 ? <div className="flex gap-1">{icons}</div> : null;
  };

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" text="Loading submissions..." />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate("/form-handlers")}
          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
        >
          <ArrowLeftIcon className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Submission Log</h1>
          {handler && (
            <p className="text-sm text-gray-500 mt-0.5">
              {handler.name} &mdash;{" "}
              <button
                onClick={() => navigate(`/form-handlers/${id}/edit`)}
                className="text-blue-600 hover:underline"
              >
                Edit handler
              </button>
            </p>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
          Failed to load submissions.
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {submissions.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            No submissions yet.
          </div>
        ) : (
          <>
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-6" />
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submitted
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {submissions.map((sub) => (
                  <React.Fragment key={sub.id}>
                    <tr
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() =>
                        setExpandedId(expandedId === sub.id ? null : sub.id)
                      }
                    >
                      <td className="px-4 py-3 text-gray-400">
                        {expandedId === sub.id ? (
                          <ChevronDownIcon className="w-4 h-4" />
                        ) : (
                          <ChevronRightIcon className="w-4 h-4" />
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                        {new Date(sub.submitted_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        {sub.contacts ? (
                          <div className="flex items-center gap-2">
                            <Link
                              to={`/contacts/${sub.contact_id}`}
                              onClick={(e) => e.stopPropagation()}
                              className="text-sm font-medium text-blue-600 hover:underline"
                            >
                              {[sub.contacts.first_name, sub.contacts.last_name]
                                .filter(Boolean)
                                .join(" ") || sub.contacts.email}
                            </Link>
                            {sub.contact_created && (
                              <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                                New
                              </span>
                            )}
                          </div>
                        ) : sub.mapped_fields?.email ? (
                          <span className="text-sm text-gray-500">
                            {String(sub.mapped_fields.email)}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400 italic">
                            unknown
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">{statusBadge(sub.status)}</td>
                      <td className="px-4 py-3">
                        {actionIcons(sub.actions_performed)}
                      </td>
                    </tr>
                    {expandedId === sub.id && (
                      <tr className="bg-gray-50">
                        <td colSpan={5} className="px-8 py-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                                Raw Payload
                              </h4>
                              <pre className="text-xs bg-white border border-gray-200 rounded p-3 overflow-auto max-h-48 text-gray-700">
                                {JSON.stringify(sub.raw_payload, null, 2)}
                              </pre>
                            </div>
                            <div>
                              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                                Actions Performed
                              </h4>
                              <pre className="text-xs bg-white border border-gray-200 rounded p-3 overflow-auto max-h-48 text-gray-700">
                                {JSON.stringify(sub.actions_performed, null, 2)}
                              </pre>
                            </div>
                          </div>
                          {sub.error_message && (
                            <div className="mt-3 p-2 bg-red-50 rounded text-xs text-red-700">
                              <strong>Error:</strong> {sub.error_message}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 text-sm text-gray-600">
                <span>
                  Showing {page * PAGE_SIZE + 1}–
                  {Math.min((page + 1) * PAGE_SIZE, total)} of {total}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() =>
                      setPage((p) => Math.min(totalPages - 1, p + 1))
                    }
                    disabled={page >= totalPages - 1}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
